import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { BookmarkPlus, Check, CheckCircle2, CircleHelp, Copy, LoaderCircle, MoreHorizontal, Pencil, RotateCcw, ScanText, Trash2, X } from "lucide-react";
import { correctOcrRegion, deletePersonalDictionary, loadKanjiDetail, loadRegionNote, loadStoredGrammarExplanation, loadStoredTranslation, lookupCompoundWords, lookupDictionary, savePersonalDictionary, saveRegionNote, saveVocabulary, setOcrRegionComprehension, updatePersonalDictionary } from "./api";
import { readerExpressionCandidates, selectCompoundSpans, type CompoundCandidate } from "./compound-words";
import { toHiragana } from "./japanese";
import { InspectableJapaneseText, JapaneseSpeechButton, KanjiDetailPopover } from "./japanese-support";
import { normalizedPolygonClipPath, normalizedPolygonPath, regionShapeViewBox } from "./region-shapes";
import { deduplicateContourText, groupRegionsByContour } from "./region-groups";
import type { SupportPanelBounds } from "./reader-occlusion";
import { nextTokenRange, splitDialogueLines, tokenSpansInSentence, type DialogueLine, type SentenceTokenSpan, type TokenRange } from "./token-selection";
import type { ComprehensionStatus, DictionaryEntry, GrammarExplanation, KanjiDetail, MangaPage, NormalizedRegionGeometry, OcrRegion, OcrToken, TranslationResult, VerbForm } from "./types";
import { SuggestedTextTargets } from "./text-targets";

export { translationModelLabel } from "./translation-model";

export type NormalizedRegion = NormalizedRegionGeometry;

export type SupportState =
  | { status: "idle" }
  | { status: "scanning"; warming: boolean }
  | { status: "error"; message: string }
  | { status: "done"; region: OcrRegion; groupedRegions?: OcrRegion[] };

interface AssistOverlayProps {
  page: MangaPage;
  enabled: boolean;
  ocrRefreshing: boolean;
  selectionBusy: boolean;
  regions: OcrRegion[];
  activeRegionId?: string;
  onSelect: (page: MangaPage, region: NormalizedRegion) => void;
  onOpenRegions: (regions: OcrRegion[]) => void;
}

export function AssistOverlay({ page, enabled, ocrRefreshing, selectionBusy, regions, activeRegionId, onSelect, onOpenRegions }: AssistOverlayProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const [start, setStart] = useState<{ x: number; y: number } | null>(null);
  const [draft, setDraft] = useState<NormalizedRegion | null>(null);
  const [focusedRegionId, setFocusedRegionId] = useState<string | null>(null);
  const pathFor = (region: OcrRegion) => normalizedPolygonPath(region.polygon, page);
  const statusClassFor = (region: OcrRegion) => region.comprehensionStatus ? `is-${region.comprehensionStatus}` : "";
  const groupedRegions = groupRegionsByContour(regions);
  const interactive = enabled && !ocrRefreshing;

  useEffect(() => {
    if (interactive) return;
    setStart(null);
    setDraft(null);
  }, [interactive]);

  const pointForEvent = (event: ReactPointerEvent<HTMLDivElement>) => {
    const bounds = overlayRef.current?.getBoundingClientRect();
    if (!bounds) return null;
    return {
      x: Math.max(0, Math.min(1, (event.clientX - bounds.left) / bounds.width)),
      y: Math.max(0, Math.min(1, (event.clientY - bounds.top) / bounds.height))
    };
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!interactive || selectionBusy || event.button !== 0) return;
    if ((event.target as HTMLElement).closest(".recognized-region")) return;
    const point = pointForEvent(event);
    if (!point) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setStart(point);
    setDraft({ x: point.x, y: point.y, width: 0, height: 0 });
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!start) return;
    const point = pointForEvent(event);
    if (!point) return;
    setDraft({
      x: Math.min(start.x, point.x),
      y: Math.min(start.y, point.y),
      width: Math.abs(point.x - start.x),
      height: Math.abs(point.y - start.y)
    });
  };

  const finishSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!start) return;
    const completed = draft;
    setStart(null);
    setDraft(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (completed && completed.width >= 0.025 && completed.height >= 0.018) onSelect(page, completed);
  };

  return (
    <div
      ref={overlayRef}
      className={`assist-overlay ${interactive ? "is-enabled" : ""}`}
      data-reader-control={interactive ? "true" : undefined}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishSelection}
      onPointerCancel={finishSelection}
      aria-hidden={!interactive}
    >
      {interactive && groupedRegions.some((group) => pathFor(group[0])) && (
        <svg
          className="region-shape-layer recognized-region-shape-layer"
          viewBox={regionShapeViewBox}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          {groupedRegions.map((group) => {
            const region = group[0];
            const path = pathFor(region);
            if (!path) return null;
            const groupKey = group.map(({ id }) => id).join(":");
            const active = group.some(({ id }) => id === activeRegionId);
            const focused = group.some(({ id }) => id === focusedRegionId);
            const statusRegion = group.find(({ comprehensionStatus }) => comprehensionStatus) ?? region;
            return (
              <path
                className={`recognized-region-shape ${statusClassFor(statusRegion)} ${active ? "is-active" : ""} ${focused ? "is-focused" : ""}`}
                d={path}
                vectorEffect="non-scaling-stroke"
                key={groupKey}
              />
            );
          })}
        </svg>
      )}
      {interactive && groupedRegions.map((group) => {
        const region = group[0];
        const groupKey = group.map(({ id }) => id).join(":");
        const active = group.some(({ id }) => id === activeRegionId);
        const statusRegion = group.find(({ comprehensionStatus }) => comprehensionStatus) ?? region;
        return (
          <button
            className={`recognized-region ${statusClassFor(statusRegion)} ${pathFor(region) ? "has-polygon" : ""} ${active ? "is-active" : ""}`}
            style={regionStyle(region)}
            type="button"
            key={groupKey}
            onPointerEnter={() => setFocusedRegionId(region.id)}
            onPointerLeave={() => setFocusedRegionId((current) => current === region.id ? null : current)}
            onFocus={() => setFocusedRegionId(region.id)}
            onBlur={() => setFocusedRegionId((current) => current === region.id ? null : current)}
            onClick={(event) => {
              event.stopPropagation();
              onOpenRegions(group);
            }}
            aria-label={`Open recognized text: ${group.map((item) => item.correctedText ?? item.text).join(" ")}`}
          />
        );
      })}
      <SuggestedTextTargets page={page} enabled={interactive} selectionBusy={selectionBusy} recognizedRegions={regions} onSelect={onSelect} />
      {interactive && draft && <span className="selection-region" style={regionStyle(draft)} />}
    </div>
  );
}

function regionStyle(region: NormalizedRegion): CSSProperties {
  const clipPath = normalizedPolygonClipPath(region.polygon, region);
  return {
    left: `${region.x * 100}%`,
    top: `${region.y * 100}%`,
    width: `${region.width * 100}%`,
    height: `${region.height * 100}%`,
    ...(clipPath ? { clipPath, WebkitClipPath: clipPath } : {})
  };
}

interface SupportPanelProps {
  state: SupportState;
  page: MangaPage | undefined;
  onUpdateRegion: (region: OcrRegion) => void;
  onUpdateComprehension: (regionId: string, status: ComprehensionStatus | null) => void;
  onActivateRegion: (region: OcrRegion) => void;
  onRedoRegion: (region: OcrRegion) => void;
  onDeleteRegion: (region: OcrRegion) => Promise<void>;
  onBoundsChange?: (bounds: SupportPanelBounds | null) => void;
  landscapeSide?: "left" | "right";
  onClose: () => void;
}

interface PhraseSuggestion {
  index: number;
  expression: string;
  addedSurface: string;
  partOfSpeech: string[];
}

interface SheetDragState {
  pointerId: number;
  startY: number;
  startHeight: number;
  startExpanded: boolean;
  moved: boolean;
}

type SupportView = "words" | "meaning" | "notes";

const supportViews: Array<{ id: SupportView; label: string }> = [
  { id: "words", label: "Words" },
  { id: "meaning", label: "Meaning" },
  { id: "notes", label: "Notes" }
];

export function VerbFormBadges({ forms, idPrefix }: { forms: VerbForm[]; idPrefix: string }) {
  const [openKind, setOpenKind] = useState<VerbForm["kind"] | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const formKey = forms.map(({ kind }) => kind).join("\u0000");
  const openForm = forms.find(({ kind }) => kind === openKind) ?? null;

  useEffect(() => setOpenKind(null), [formKey]);
  useEffect(() => {
    if (!openKind) return;
    const closeOutside = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpenKind(null);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpenKind(null);
    };
    document.addEventListener("pointerdown", closeOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [openKind]);

  if (forms.length === 0) return null;
  const popupId = openForm ? `${idPrefix}-verb-form-${openForm.kind}` : undefined;
  return (
    <div className="verb-form-badges" ref={containerRef}>
      {forms.map((form) => (
        <button
          className={`verb-form-badge is-${form.kind}`}
          type="button"
          key={form.kind}
          aria-expanded={openKind === form.kind}
          aria-controls={openKind === form.kind ? popupId : undefined}
          onClick={() => setOpenKind((current) => current === form.kind ? null : form.kind)}
        >
          {form.label}
        </button>
      ))}
      {openForm && (
        <div className={`verb-form-popover is-${openForm.kind}`} id={popupId} role="dialog" aria-label={`${openForm.label} verb form`}>
          <strong>{openForm.label}</strong>
          <p>{openForm.explanation}</p>
        </div>
      )}
    </div>
  );
}

export function SupportPanel({
  state,
  page,
  onUpdateRegion,
  onUpdateComprehension,
  onActivateRegion,
  onRedoRegion,
  onDeleteRegion,
  onBoundsChange,
  landscapeSide = "right",
  onClose
}: SupportPanelProps) {
  const detailRegions = state.status === "done"
    ? deduplicateContourText(
        state.groupedRegions?.length ? state.groupedRegions : [state.region],
        state.region.id
      )
    : [];
  const detailSegments: Array<{ region: OcrRegion; text: string; start: number; end: number }> = [];
  const displayTokenSources: Array<{ token: OcrToken; region: OcrRegion | null }> = [];
  let displayOffset = 0;
  detailRegions.forEach((region, index) => {
    const text = region.correctedText ?? region.text;
    if (index > 0) {
      displayOffset += 1;
      displayTokenSources.push({
        token: { surface: "\n", lemma: "", reading: null, partOfSpeech: [] },
        region: null
      });
    }
    detailSegments.push({ region, text, start: displayOffset, end: displayOffset + text.length });
    displayOffset += text.length;
    displayTokenSources.push(...region.tokens.map((token) => ({ token, region })));
  });
  const displaySentence = detailSegments.map(({ text }) => text).join("\n");
  const tokenItems = displayTokenSources
    .map(({ token, region }, sourceIndex) => ({ token, region, sourceIndex }))
    .filter(({ token, region }) => region && isUsefulToken(token)) as Array<{
      token: OcrToken;
      region: OcrRegion;
      sourceIndex: number;
    }>;
  const [activeTokenRange, setActiveTokenRange] = useState<TokenRange | null>(null);
  const [entries, setEntries] = useState<DictionaryEntry[]>([]);
  const [dictionaryLoading, setDictionaryLoading] = useState(false);
  const [dictionaryMessage, setDictionaryMessage] = useState<string | null>(null);
  const [dictionaryMissing, setDictionaryMissing] = useState(false);
  const [saved, setSaved] = useState(false);
  const [vocabularySaving, setVocabularySaving] = useState(false);
  const [vocabularyError, setVocabularyError] = useState<string | null>(null);
  const [customDefinition, setCustomDefinition] = useState("");
  const [customScope, setCustomScope] = useState<"series" | "global">("series");
  const [personalAdding, setPersonalAdding] = useState(false);
  const [personalEditing, setPersonalEditing] = useState(false);
  const [personalDefinitionDraft, setPersonalDefinitionDraft] = useState("");
  const [personalEditArmed, setPersonalEditArmed] = useState(false);
  const [selectionBusy, setSelectionBusy] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const [removeArmed, setRemoveArmed] = useState(false);
  const [comprehensionSaving, setComprehensionSaving] = useState(false);
  const [comprehensionError, setComprehensionError] = useState<string | null>(null);
  const [translation, setTranslation] = useState<TranslationResult | null>(null);
  const [translationLoading, setTranslationLoading] = useState(false);
  const [translationError, setTranslationError] = useState<string | null>(null);
  const [explanation, setExplanation] = useState<GrammarExplanation | null>(null);
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationError, setExplanationError] = useState<string | null>(null);
  const [sentenceCopied, setSentenceCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [sentenceEditing, setSentenceEditing] = useState(false);
  const [sentenceDraft, setSentenceDraft] = useState("");
  const [sentenceSaving, setSentenceSaving] = useState(false);
  const [sentenceEditError, setSentenceEditError] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [savedNote, setSavedNote] = useState("");
  const [noteLoading, setNoteLoading] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [supportView, setSupportView] = useState<SupportView>("words");
  const [sheetExpanded, setSheetExpanded] = useState(false);
  const [sheetDragHeight, setSheetDragHeight] = useState<number | null>(null);
  const [phraseSuggestions, setPhraseSuggestions] = useState<PhraseSuggestion[]>([]);
  const [compoundSpans, setCompoundSpans] = useState<CompoundCandidate[]>([]);
  const [kanjiLiteral, setKanjiLiteral] = useState<string | null>(null);
  const [kanjiDetail, setKanjiDetail] = useState<KanjiDetail | null>(null);
  const [kanjiLoading, setKanjiLoading] = useState(false);
  const [kanjiError, setKanjiError] = useState<string | null>(null);
  const translationRequest = useRef(0);
  const explanationRequest = useRef(0);
  const noteRequest = useRef(0);
  const removeArmTimer = useRef<number | null>(null);
  const personalRemoveTimer = useRef<number | null>(null);
  const copyTimer = useRef<number | null>(null);
  const kanjiRequest = useRef(0);
  const suppressNextTokenClick = useRef(false);
  const suppressTokenClickTimer = useRef<number | null>(null);
  const sentenceSaveRequest = useRef(0);
  const comprehensionRequestSequence = useRef(0);
  const latestComprehensionRequest = useRef(new Map<string, number>());
  const confirmedComprehensionStatus = useRef(new Map<string, ComprehensionStatus | null>());
  const panelRef = useRef<HTMLElement>(null);
  const supportTabsRef = useRef<HTMLDivElement>(null);
  const supportOptionsRef = useRef<HTMLDetailsElement>(null);
  const sheetDrag = useRef<SheetDragState | null>(null);
  const suppressHandleClick = useRef(false);
  const currentRegionIdRef = useRef<string | undefined>(undefined);
  const dictionaryRegionIdRef = useRef<string | undefined>(undefined);
  const activeExpressionRef = useRef<string | undefined>(undefined);
  const primaryEntry = entries[0];
  const sentence = state.status === "done" ? state.region.correctedText ?? state.region.text : "";
  const activeTokenItems = activeTokenRange ? tokenItems.slice(activeTokenRange.start, activeTokenRange.end + 1) : [];
  const activeTokenRegion = activeTokenItems[0]?.region;
  const currentRegionId = state.status === "done" ? state.region.id : undefined;
  const dictionaryRegionId = activeTokenRegion?.id ?? currentRegionId;
  const dictionarySentence = activeTokenRegion
    ? activeTokenRegion.correctedText ?? activeTokenRegion.text
    : sentence;
  const activeTokenSourceIndexes = new Set(activeTokenItems.map(({ sourceIndex }) => sourceIndex));
  const sentenceTokenSpans = state.status === "done"
    ? tokenSpansInSentence(displaySentence, displayTokenSources.map(({ token }) => token.surface))
    : [];
  const dialogueLines = splitDialogueLines(displaySentence);
  const tokenLineGroups = (() => {
    const groups = dialogueLines.map((line) => ({ ...line, tokenIndexes: [] as number[] }));
    const assigned = new Set<number>();
    tokenItems.forEach(({ sourceIndex }, tokenIndex) => {
      const span = sentenceTokenSpans.find((candidate) => candidate.sourceIndex === sourceIndex);
      if (!span) return;
      const lineIndex = dialogueLines.findIndex((line) => span.start < line.end && span.end > line.start);
      if (lineIndex < 0) return;
      groups[lineIndex].tokenIndexes.push(tokenIndex);
      assigned.add(tokenIndex);
    });
    const unassigned = tokenItems.map((_, index) => index).filter((index) => !assigned.has(index));
    if (unassigned.length > 0) {
      if (groups.length === 0) groups.push({ start: 0, end: displaySentence.length, text: displaySentence, tokenIndexes: [] });
      groups[groups.length - 1].tokenIndexes.push(...unassigned);
    }
    return groups.filter((group) => group.tokenIndexes.length > 0);
  })();
  const activeExpression = activeTokenItems.length === 1
    ? activeTokenItems[0].token.lemma || activeTokenItems[0].token.surface
    : activeTokenItems.map(({ token }) => token.surface).join("") || undefined;
  const activeSurface = activeTokenItems.map(({ token }) => token.surface).join("") || undefined;
  currentRegionIdRef.current = currentRegionId;
  dictionaryRegionIdRef.current = dictionaryRegionId;
  activeExpressionRef.current = activeExpression;
  const combinedReading = activeTokenItems.map(({ token }) => token.reading).filter(Boolean).join("");
  const rawActiveReading = combinedReading || primaryEntry?.readings[0];
  const activeReading = rawActiveReading ? toHiragana(rawActiveReading) : undefined;
  const activePartOfSpeech = [...new Set(activeTokenItems.flatMap(({ token }) => token.partOfSpeech))];
  const adjacentPhraseCandidates = phraseCandidates(tokenItems, activeTokenRange);
  const phraseCandidateKey = adjacentPhraseCandidates
    .map((candidate) => `${candidate.index}:${candidate.expression}:${candidate.partOfSpeech.join(",")}`)
    .join("\u0000");

  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!panel || state.status === "idle") {
      onBoundsChange?.(null);
      return;
    }

    const reportBounds = () => {
      const bounds = panel.getBoundingClientRect();
      onBoundsChange?.({
        top: bounds.top,
        right: bounds.right,
        bottom: bounds.bottom,
        left: bounds.left,
        viewportHeight: window.visualViewport?.height ?? window.innerHeight
      });
    };
    reportBounds();
    const resizeObserver = new ResizeObserver(reportBounds);
    resizeObserver.observe(panel);
    window.addEventListener("resize", reportBounds);
    window.visualViewport?.addEventListener("resize", reportBounds);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", reportBounds);
      window.visualViewport?.removeEventListener("resize", reportBounds);
    };
  }, [onBoundsChange, state.status]);
  const suggestedByIndex = new Map(phraseSuggestions.map((suggestion) => [suggestion.index, suggestion]));
  const primaryPhraseSuggestion = phraseSuggestions[0];
  const availableCompoundCandidates = readerExpressionCandidates(tokenItems);
  const compoundCandidateKey = availableCompoundCandidates.map(({ start, end, expression }) => `${start}:${end}:${expression}`).join("\u0000");
  const resolvedDictionaryForm = primaryEntry?.lookupExpression;
  const activeWrittenForm = resolvedDictionaryForm ?? primaryEntry?.kanji.find((form) => form !== activeExpression);
  const dictionaryReading = primaryEntry?.lookupRelation === "potential" && primaryEntry.readings[0]
    ? toHiragana(primaryEntry.readings[0])
    : activeReading;
  const dictionaryMeta = [
    primaryEntry?.lookupRelation === "potential" && activeWrittenForm ? `Potential form of ${activeWrittenForm}` : activeWrittenForm,
    dictionaryReading
  ].filter(Boolean).join(" · ");
  const primaryDefinition = primaryEntry?.definitions.slice(0, 3).join("; ");
  const noteDirty = note !== savedNote;
  const hasSavedNote = Boolean(savedNote.trim());
  useEffect(() => {
    const controller = new AbortController();
    setActiveTokenRange(null);
    setSaved(false);
    setVocabularySaving(false);
    setVocabularyError(null);
    setCustomDefinition("");
    setCustomScope("series");
    setPersonalAdding(false);
    setPersonalEditing(false);
    setPersonalDefinitionDraft("");
    setPersonalEditArmed(false);
    setSelectionBusy(false);
    setSelectionError(null);
    setRemoveArmed(false);
    setComprehensionSaving(false);
    setComprehensionError(null);
    if (state.status !== "done") setSupportView("words");
    panelRef.current?.scrollTo({ top: 0, behavior: "instant" });
    if (removeArmTimer.current !== null) window.clearTimeout(removeArmTimer.current);
    if (personalRemoveTimer.current !== null) window.clearTimeout(personalRemoveTimer.current);
    setTranslation(null);
    setTranslationLoading(state.status === "done");
    setTranslationError(null);
    setExplanation(null);
    setExplanationLoading(state.status === "done");
    setExplanationError(null);
    setSentenceCopied(false);
    setCopyError(null);
    setSentenceEditing(false);
    setSentenceDraft("");
    setSentenceSaving(false);
    setSentenceEditError(null);
    ++sentenceSaveRequest.current;
    setPhraseSuggestions([]);
    setCompoundSpans([]);
    setKanjiLiteral(null);
    setKanjiDetail(null);
    setKanjiLoading(false);
    setKanjiError(null);
    ++kanjiRequest.current;
    const requestId = ++translationRequest.current;
    const explanationRequestId = ++explanationRequest.current;
    if (state.status === "done") {
      loadStoredTranslation(state.region.id, controller.signal)
        .then((result) => {
          if (requestId !== translationRequest.current) return;
          setTranslation(result);
        })
        .catch((error) => {
          if (
            requestId === translationRequest.current &&
            !(error instanceof DOMException && error.name === "AbortError")
          ) {
            setTranslationError(error instanceof Error ? error.message : "Could not load the saved translation");
          }
        })
        .finally(() => {
          if (requestId === translationRequest.current) setTranslationLoading(false);
        });
      loadStoredGrammarExplanation(state.region.id, controller.signal)
        .then((result) => {
          if (explanationRequestId === explanationRequest.current) setExplanation(result);
        })
        .catch((error) => {
          if (
            explanationRequestId === explanationRequest.current &&
            !(error instanceof DOMException && error.name === "AbortError")
          ) {
            setExplanationError(error instanceof Error ? error.message : "Could not load the saved explanation");
          }
        })
        .finally(() => {
          if (explanationRequestId === explanationRequest.current) setExplanationLoading(false);
        });
    }
    return () => controller.abort();
  }, [state.status === "done" ? state.region.id : state.status]);

  useEffect(() => {
    const controller = new AbortController();
    setNote("");
    setSavedNote("");
    setNoteError(null);
    setNoteSaving(false);
    const requestId = ++noteRequest.current;
    if (state.status !== "done") {
      setNoteLoading(false);
      return () => controller.abort();
    }
    setNoteLoading(true);
    loadRegionNote(state.region.id, controller.signal)
      .then((result) => {
        if (requestId !== noteRequest.current) return;
        setNote(result);
        setSavedNote(result);
      })
      .catch((error) => {
        if (
          requestId === noteRequest.current &&
          !(error instanceof DOMException && error.name === "AbortError")
        ) {
          setNoteError(error instanceof Error ? error.message : "Could not load this note");
        }
      })
      .finally(() => {
        if (requestId === noteRequest.current) setNoteLoading(false);
      });
    return () => controller.abort();
  }, [state.status === "done" ? state.region.id : state.status]);

  useEffect(() => () => {
    if (removeArmTimer.current !== null) window.clearTimeout(removeArmTimer.current);
    if (personalRemoveTimer.current !== null) window.clearTimeout(personalRemoveTimer.current);
    if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
    if (suppressTokenClickTimer.current !== null) window.clearTimeout(suppressTokenClickTimer.current);
  }, []);

  useEffect(() => {
    const closeOnOutsidePress = (event: PointerEvent) => {
      const options = supportOptionsRef.current;
      if (options?.open && event.target instanceof Node && !options.contains(event.target)) options.open = false;
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      const options = supportOptionsRef.current;
      if (event.key !== "Escape" || !options?.open) return;
      event.stopPropagation();
      options.open = false;
      options.querySelector<HTMLElement>("summary")?.focus();
    };
    document.addEventListener("pointerdown", closeOnOutsidePress);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsidePress);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, []);

  const updateComprehension = async (status: ComprehensionStatus): Promise<boolean> => {
    if (state.status !== "done" || comprehensionSaving) return false;
    const previous = state.region;
    const nextStatus = previous.comprehensionStatus === status ? null : status;
    if (!confirmedComprehensionStatus.current.has(previous.id)) {
      confirmedComprehensionStatus.current.set(previous.id, previous.comprehensionStatus);
    }
    const requestId = ++comprehensionRequestSequence.current;
    latestComprehensionRequest.current.set(previous.id, requestId);
    setComprehensionSaving(true);
    setComprehensionError(null);
    onUpdateComprehension(previous.id, nextStatus);
    try {
      const updated = await setOcrRegionComprehension(previous.id, nextStatus);
      confirmedComprehensionStatus.current.set(previous.id, updated.comprehensionStatus);
      if (
        latestComprehensionRequest.current.get(previous.id) !== requestId
        || currentRegionIdRef.current !== previous.id
      ) return false;
      onUpdateComprehension(previous.id, updated.comprehensionStatus);
      return true;
    } catch (error) {
      if (latestComprehensionRequest.current.get(previous.id) !== requestId) return false;
      onUpdateComprehension(
        previous.id,
        confirmedComprehensionStatus.current.get(previous.id) ?? null
      );
      if (currentRegionIdRef.current === previous.id) {
        setComprehensionError(error instanceof Error ? error.message : "Could not save how this line felt");
      }
      return false;
    } finally {
      if (
        latestComprehensionRequest.current.get(previous.id) === requestId
        && currentRegionIdRef.current === previous.id
      ) {
        setComprehensionSaving(false);
      }
    }
  };

  const chooseComprehension = async (status: ComprehensionStatus) => {
    if (await updateComprehension(status)) onClose();
  };

  const refreshActiveDictionary = async () => {
    const expression = activeExpression;
    const regionId = dictionaryRegionId;
    if (!expression) return false;
    const results = await lookupDictionary(expression, activePartOfSpeech, undefined, regionId, activeSurface);
    if (activeExpressionRef.current !== expression || dictionaryRegionIdRef.current !== regionId) return false;
    setEntries(results);
    setDictionaryMissing(results.length === 0);
    setDictionaryMessage(results.length === 0 ? "No dictionary entry found" : null);
    return true;
  };

  const savePersonalEdit = async () => {
    if (!primaryEntry || primaryEntry.source !== "personal" || !primaryEntry.personalEntryId || vocabularySaving || !personalDefinitionDraft.trim()) return;
    const regionId = dictionaryRegionId;
    const expression = activeExpression;
    setVocabularySaving(true);
    setVocabularyError(null);
    try {
      await updatePersonalDictionary(primaryEntry.personalEntryId, {
        definition: personalDefinitionDraft.trim(),
        reading: activeReading
      });
      if (!await refreshActiveDictionary()) return;
      setPersonalEditing(false);
      setPersonalEditArmed(false);
      setTranslation(null);
      setExplanation(null);
    } catch (error) {
      if (dictionaryRegionIdRef.current === regionId && activeExpressionRef.current === expression) {
        setVocabularyError(error instanceof Error ? error.message : "Could not update this meaning");
      }
    } finally {
      if (dictionaryRegionIdRef.current === regionId && activeExpressionRef.current === expression) setVocabularySaving(false);
    }
  };

  const removePersonalMeaning = async () => {
    if (!primaryEntry || primaryEntry.source !== "personal" || !primaryEntry.personalEntryId || vocabularySaving) return;
    if (!personalEditArmed) {
      setPersonalEditArmed(true);
      if (personalRemoveTimer.current !== null) window.clearTimeout(personalRemoveTimer.current);
      personalRemoveTimer.current = window.setTimeout(() => {
        setPersonalEditArmed(false);
        personalRemoveTimer.current = null;
      }, 5_000);
      return;
    }
    const regionId = dictionaryRegionId;
    const expression = activeExpression;
    setVocabularySaving(true);
    setVocabularyError(null);
    try {
      await deletePersonalDictionary(primaryEntry.personalEntryId);
      if (!await refreshActiveDictionary()) return;
      setPersonalEditing(false);
      setPersonalEditArmed(false);
      setTranslation(null);
      setExplanation(null);
    } catch (error) {
      if (dictionaryRegionIdRef.current === regionId && activeExpressionRef.current === expression) {
        setVocabularyError(error instanceof Error ? error.message : "Could not remove this meaning");
      }
    } finally {
      if (dictionaryRegionIdRef.current === regionId && activeExpressionRef.current === expression) setVocabularySaving(false);
    }
  };

  useEffect(() => {
    setSaved(false);
    setVocabularyError(null);
    setCustomDefinition("");
    setPersonalAdding(false);
    setPersonalEditing(false);
    setPersonalDefinitionDraft("");
    setPersonalEditArmed(false);
    setDictionaryMissing(false);
    if (!activeExpression) {
      setEntries([]);
      setDictionaryLoading(false);
      setDictionaryMessage(null);
      return;
    }
    const controller = new AbortController();
    let current = true;
    setDictionaryLoading(true);
    setDictionaryMessage(null);
    setEntries([]);
    lookupDictionary(activeExpression, activePartOfSpeech, controller.signal, dictionaryRegionId, activeSurface)
      .then((results) => {
        if (!current) return;
        setEntries(results);
        if (results.length === 0) {
          setDictionaryMissing(true);
          setDictionaryMessage("No dictionary entry found");
        }
      })
      .catch((error) => {
        if (current && !(error instanceof DOMException && error.name === "AbortError")) {
          setEntries([]);
          setDictionaryMessage(error instanceof Error ? error.message : "Dictionary lookup failed");
        }
      })
      .finally(() => {
        if (current) setDictionaryLoading(false);
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [activeExpression, activeSurface, activePartOfSpeech.join("\u0000"), dictionaryRegionId]);

  useEffect(() => {
    if (adjacentPhraseCandidates.length === 0) {
      setPhraseSuggestions([]);
      return;
    }
    const controller = new AbortController();
    let current = true;
    setPhraseSuggestions([]);
    Promise.all(adjacentPhraseCandidates.map(async (candidate) => ({
      candidate,
      entries: await lookupDictionary(candidate.expression, candidate.partOfSpeech, controller.signal, dictionaryRegionId)
    })))
      .then((results) => {
        if (current) setPhraseSuggestions(results.filter(({ entries }) => entries.length > 0).map(({ candidate }) => candidate));
      })
      .catch((error) => {
        if (current && !(error instanceof DOMException && error.name === "AbortError")) setPhraseSuggestions([]);
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [phraseCandidateKey, dictionaryRegionId]);

  useEffect(() => {
    if (availableCompoundCandidates.length === 0) {
      setCompoundSpans([]);
      return;
    }
    const controller = new AbortController();
    let current = true;
    setCompoundSpans([]);
    lookupCompoundWords(availableCompoundCandidates.map(({ expression }) => expression), controller.signal, dictionaryRegionId)
      .then((matches) => {
        if (current) setCompoundSpans(selectCompoundSpans(availableCompoundCandidates, new Set(matches)));
      })
      .catch((error) => {
        if (current && !(error instanceof DOMException && error.name === "AbortError")) setCompoundSpans([]);
      });
    return () => {
      current = false;
      controller.abort();
    };
  }, [compoundCandidateKey, dictionaryRegionId]);

  const removeCurrentSelection = async () => {
    if (state.status !== "done" || selectionBusy) return;
    if (!removeArmed) {
      setRemoveArmed(true);
      if (removeArmTimer.current !== null) window.clearTimeout(removeArmTimer.current);
      removeArmTimer.current = window.setTimeout(() => {
        setRemoveArmed(false);
        removeArmTimer.current = null;
      }, 5000);
      return;
    }
    if (removeArmTimer.current !== null) window.clearTimeout(removeArmTimer.current);
    removeArmTimer.current = null;
    setRemoveArmed(false);
    setSelectionBusy(true);
    setSelectionError(null);
    try {
      await onDeleteRegion(state.region);
    } catch (error) {
      setSelectionBusy(false);
      setSelectionError(error instanceof Error ? error.message : "Could not remove the selection");
    }
  };

  const selectToken = (index: number, expressionSpan?: CompoundCandidate) => {
    if (vocabularySaving || sentenceSaving) return;
    if (expressionSpan) {
      const alreadySelected = activeTokenRange?.start === expressionSpan.start && activeTokenRange.end === expressionSpan.end;
      setPhraseSuggestions([]);
      setActiveTokenRange(alreadySelected ? null : { start: expressionSpan.start, end: expressionSpan.end });
      return;
    }
    const validCombinations = new Set(phraseSuggestions.map((candidate) => candidate.index));
    const next = nextTokenRange(activeTokenRange, index, validCombinations);
    setPhraseSuggestions([]);
    setActiveTokenRange(next);
  };

  const saveWord = async (definition?: string, customDefinition = false) => {
    if (state.status !== "done" || !page || activeTokenItems.length === 0 || !activeExpression) return;
    let createdPersonalEntryId: string | null = null;
    setVocabularySaving(true);
    setVocabularyError(null);
    const regionId = dictionaryRegionId;
    const expression = activeExpression;
    try {
      if (customDefinition) {
        const personalDefinition = definition?.trim();
        if (!personalDefinition) return;
        const personalEntry = await savePersonalDictionary({
          expression: activeExpression,
          reading: activeReading ?? undefined,
          definition: personalDefinition,
          scope: customScope,
          regionId: dictionaryRegionId
        });
        createdPersonalEntryId = personalEntry.id;
      } else {
        await saveVocabulary({
          expression: activeExpression,
          reading: activeReading ?? undefined,
          definition: definition?.trim() || undefined,
          sentence: dictionarySentence,
          pageId: page.id,
          regionId: dictionaryRegionId
        });
      }
      if (dictionaryRegionIdRef.current !== regionId || activeExpressionRef.current !== expression) return;
      if (!customDefinition) setSaved(true);
      const personalDefinition = definition?.trim();
      if (personalDefinition && customDefinition && createdPersonalEntryId) {
        setEntries([{
          id: `personal:${createdPersonalEntryId}`,
          personalEntryId: createdPersonalEntryId,
          kanji: [activeExpression],
          readings: activeReading ? [activeReading] : [],
          definitions: [personalDefinition],
          senses: [{ definitions: [personalDefinition], labels: [customScope === "series" ? "This series" : "Everywhere"] }],
          common: false,
          source: "personal",
          personalScope: customScope,
          verbForms: primaryEntry?.verbForms ?? []
        }]);
        setDictionaryMissing(false);
        setDictionaryMessage(null);
        setPersonalAdding(false);
        setCustomDefinition("");
      }
    } catch (error) {
      if (dictionaryRegionIdRef.current === regionId && activeExpressionRef.current === expression) {
        setVocabularyError(error instanceof Error ? error.message : "Could not save this word");
      }
    } finally {
      if (dictionaryRegionIdRef.current === regionId && activeExpressionRef.current === expression) setVocabularySaving(false);
    }
  };

  const inspectKanji = async (literal: string) => {
    suppressNextTokenClick.current = true;
    if (suppressTokenClickTimer.current !== null) window.clearTimeout(suppressTokenClickTimer.current);
    suppressTokenClickTimer.current = window.setTimeout(() => {
      suppressNextTokenClick.current = false;
      suppressTokenClickTimer.current = null;
    }, 1_000);
    const requestId = ++kanjiRequest.current;
    setKanjiLiteral(literal);
    setKanjiDetail(null);
    setKanjiError(null);
    setKanjiLoading(true);
    try {
      const result = await loadKanjiDetail(literal);
      if (requestId !== kanjiRequest.current) return;
      setKanjiDetail(result);
      if (!result) setKanjiError("No kanji details found.");
    } catch (error) {
      if (requestId === kanjiRequest.current) {
        setKanjiError(error instanceof Error ? error.message : "Could not load kanji details");
      }
    } finally {
      if (requestId === kanjiRequest.current) setKanjiLoading(false);
    }
  };

  const renderTokenButton = (index: number) => {
    const compound = compoundSpans.find(({ start, end }) => index >= start && index <= end);
    if (compound && index !== compound.start) return null;
    const renderedItems = compound ? tokenItems.slice(compound.start, compound.end + 1) : [tokenItems[index]];
    const token = compound ? {
      surface: compound.expression,
      lemma: compound.expression,
      reading: renderedItems.map(({ token: item }) => item.reading ?? "").join("") || null,
      partOfSpeech: [...new Set(renderedItems.flatMap(({ token: item }) => item.partOfSpeech))]
    } : tokenItems[index].token;
    const selected = compound
      ? Boolean(activeTokenRange && activeTokenRange.start <= compound.start && activeTokenRange.end >= compound.end)
      : Boolean(activeTokenRange && index >= activeTokenRange.start && index <= activeTokenRange.end);
    const suggestion = suggestedByIndex.get(index);
    const tokenReading = token.reading ? toHiragana(token.reading) : null;
    const showTokenReading = tokenReading && tokenReading !== toHiragana(token.surface);
    const accessibilityHint = suggestion
      ? `Add it to look up ${suggestion.expression} together.`
      : compound
        ? `Dictionary expression. Tap to look it up as one unit.`
        : "";
    return (
      <button
        className={[selected ? "selected" : "", suggestion ? "phrase-suggestion" : "", compound ? "compound-preview dictionary-expression" : ""].filter(Boolean).join(" ")}
        type="button"
        key={`${token.surface}-${compound?.start ?? index}-${compound?.end ?? index}`}
        onClick={() => {
          if (suppressNextTokenClick.current) {
            suppressNextTokenClick.current = false;
            if (suppressTokenClickTimer.current !== null) window.clearTimeout(suppressTokenClickTimer.current);
            suppressTokenClickTimer.current = null;
            return;
          }
          selectToken(index, compound);
        }}
        aria-pressed={selected}
        disabled={sentenceSaving || vocabularySaving}
        aria-label={accessibilityHint ? `${token.surface}${showTokenReading ? ` ${tokenReading}` : ""}. ${accessibilityHint}` : undefined}
        title={compound
          ? `${compound.expression} is a combined dictionary expression.${/\p{Script=Han}/u.test(token.surface) ? " Hold a kanji for details." : ""}`
          : /\p{Script=Han}/u.test(token.surface) ? "Hold a kanji for details" : undefined}
        lang="ja"
      >
        {suggestion && <i aria-hidden="true">+</i>}
        <InspectableJapaneseText text={token.surface} onInspect={(literal) => void inspectKanji(literal)} />
        {showTokenReading && <small>{tokenReading}</small>}
      </button>
    );
  };

  const beginSentenceEdit = () => {
    if (state.status !== "done" || sentenceSaving) return;
    setSentenceDraft(sentence);
    setSentenceEditError(null);
    setSentenceEditing(true);
  };

  const beginSentenceEditFromOptions = () => {
    if (supportOptionsRef.current) supportOptionsRef.current.open = false;
    beginSentenceEdit();
  };

  const cancelSentenceEdit = () => {
    if (sentenceSaving) return;
    setSentenceDraft(sentence);
    setSentenceEditError(null);
    setSentenceEditing(false);
  };

  const saveSentenceCorrection = async () => {
    if (state.status !== "done" || sentenceSaving) return;
    const correctedText = sentenceDraft.trim();
    if (!correctedText || correctedText === sentence.trim()) return;
    ++translationRequest.current;
    ++explanationRequest.current;
    setTranslationLoading(false);
    setExplanationLoading(false);
    setSentenceSaving(true);
    setSentenceEditError(null);
    const regionId = state.region.id;
    const requestId = ++sentenceSaveRequest.current;
    try {
      const updated = await correctOcrRegion(regionId, correctedText);
      onUpdateRegion(updated);
      if (requestId !== sentenceSaveRequest.current || currentRegionIdRef.current !== regionId) return;
      setTranslation(null);
      setTranslationError(null);
      setExplanation(null);
      setExplanationError(null);
      setActiveTokenRange(null);
      setEntries([]);
      setDictionaryMessage(null);
      setPhraseSuggestions([]);
      setCompoundSpans([]);
      setSentenceCopied(false);
      setCopyError(null);
      setSentenceDraft(updated.correctedText ?? updated.text);
      setSentenceEditing(false);
    } catch (error) {
      if (requestId === sentenceSaveRequest.current && currentRegionIdRef.current === regionId) {
        setSentenceEditError(error instanceof Error ? error.message : "Could not save the corrected text");
      }
    } finally {
      if (requestId === sentenceSaveRequest.current && currentRegionIdRef.current === regionId) setSentenceSaving(false);
    }
  };

  const copySentence = async () => {
    if (!sentence.trim()) return;
    setCopyError(null);
    try {
      await navigator.clipboard.writeText(sentence);
      setSentenceCopied(true);
      if (copyTimer.current !== null) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => {
        setSentenceCopied(false);
        copyTimer.current = null;
      }, 1800);
    } catch {
      setCopyError("Could not copy this text");
    }
  };

  const saveNote = async () => {
    if (state.status !== "done" || noteSaving || note === savedNote) return;
    const noteToSave = note;
    const requestId = ++noteRequest.current;
    setNoteSaving(true);
    setNoteError(null);
    try {
      const stored = await saveRegionNote(state.region.id, noteToSave);
      if (requestId === noteRequest.current) setSavedNote(stored);
    } catch (error) {
      if (requestId === noteRequest.current) {
        setNoteError(error instanceof Error ? error.message : "Could not save this note");
      }
    } finally {
      if (requestId === noteRequest.current) setNoteSaving(false);
    }
  };

  const startSheetDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (!event.isPrimary || event.button !== 0 || !panelRef.current) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    sheetDrag.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startHeight: panelRef.current.getBoundingClientRect().height,
      startExpanded: sheetExpanded,
      moved: false
    };
  };

  const moveSheetDrag = (event: ReactPointerEvent<HTMLButtonElement>) => {
    const drag = sheetDrag.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = drag.startY - event.clientY;
    if (Math.abs(delta) > 6) drag.moved = true;
    if (!drag.moved) return;
    const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
    const reducedHeight = Math.min(viewportHeight * 0.48, 520);
    const fullHeight = Math.max(reducedHeight, viewportHeight - 12);
    setSheetDragHeight(Math.max(reducedHeight, Math.min(fullHeight, drag.startHeight + delta)));
  };

  const finishSheetDrag = (event: ReactPointerEvent<HTMLButtonElement>, cancelled = false) => {
    const drag = sheetDrag.current;
    if (!drag || drag.pointerId !== event.pointerId) return;
    const delta = drag.startY - event.clientY;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (drag.moved) {
      suppressHandleClick.current = true;
      const expanded = cancelled ? drag.startExpanded : delta > 48 ? true : delta < -48 ? false : drag.startExpanded;
      setSheetExpanded(expanded);
    }
    setSheetDragHeight(null);
    sheetDrag.current = null;
  };

  const chooseSupportView = (view: SupportView) => {
    setSupportView(view);
    const panel = panelRef.current;
    const tabs = supportTabsRef.current;
    if (!panel || !tabs || panel.scrollTop <= tabs.offsetTop) return;
    panel.scrollTo({ top: Math.max(0, tabs.offsetTop - 14), behavior: "smooth" });
  };

  const navigateSupportViews = (event: ReactKeyboardEvent<HTMLButtonElement>, view: SupportView) => {
    const currentIndex = supportViews.findIndex((option) => option.id === view);
    const requestedIndex = event.key === "Home"
      ? 0
      : event.key === "End"
        ? supportViews.length - 1
        : event.key === "ArrowRight" || event.key === "ArrowDown"
          ? (currentIndex + 1) % supportViews.length
          : event.key === "ArrowLeft" || event.key === "ArrowUp"
            ? (currentIndex - 1 + supportViews.length) % supportViews.length
            : -1;
    if (requestedIndex < 0) return;
    event.preventDefault();
    chooseSupportView(supportViews[requestedIndex].id);
    supportTabsRef.current?.querySelectorAll<HTMLButtonElement>("[role=tab]")[requestedIndex]?.focus();
  };

  if (state.status === "idle") return null;

  return (
    <aside
      ref={panelRef}
      className={`support-panel is-side-${landscapeSide} ${sheetExpanded ? "is-expanded" : ""} ${sheetDragHeight !== null ? "is-dragging" : ""}`}
      style={sheetDragHeight === null ? undefined : { "--support-sheet-height": `${sheetDragHeight}px` } as CSSProperties}
      data-reader-control
      aria-label="Details"
    >
      <button
        className="support-handle"
        type="button"
        onPointerDown={startSheetDrag}
        onPointerMove={moveSheetDrag}
        onPointerUp={(event) => finishSheetDrag(event)}
        onPointerCancel={(event) => finishSheetDrag(event, true)}
        onClick={() => {
          if (suppressHandleClick.current) {
            suppressHandleClick.current = false;
            return;
          }
          setSheetExpanded((expanded) => !expanded);
        }}
        aria-label={sheetExpanded ? "Reduce details panel" : "Expand details panel"}
        aria-expanded={sheetExpanded}
      />
      <header className="support-header">
        <div className="support-header-actions">
          <button className="support-icon-button support-close-button" type="button" onClick={onClose} aria-label="Close details" title="Close details"><X size={20} /></button>
          {state.status === "done" && (
            <JapaneseSpeechButton text={displaySentence} mode="sentence" label="Hear speech bubble in Japanese" compact className="support-header-speech-button" />
          )}
          {state.status === "done" && (
            <details className="support-options" key={state.region.id} ref={supportOptionsRef}>
              <summary className="support-icon-button" aria-label="Selection options" title="Selection options">
                <MoreHorizontal size={20} />
              </summary>
              <div className="support-options-menu">
                <div className="line-status-controls" role="group" aria-label="Comprehension for this line" aria-busy={comprehensionSaving}>
                  <button
                    className={`line-status-button is-understood ${state.region.comprehensionStatus === "understood" ? "is-selected" : ""}`}
                    type="button"
                    aria-pressed={state.region.comprehensionStatus === "understood"}
                    aria-label={state.region.comprehensionStatus === "understood" ? "Clear understood status" : "Mark this line as understood"}
                    onClick={() => void chooseComprehension("understood")}
                    disabled={comprehensionSaving}
                  >
                    <CheckCircle2 size={16} /> Understood
                  </button>
                  <button
                    className={`line-status-button is-unsure ${state.region.comprehensionStatus === "unsure" ? "is-selected" : ""}`}
                    type="button"
                    aria-pressed={state.region.comprehensionStatus === "unsure"}
                    aria-label={state.region.comprehensionStatus === "unsure" ? "Clear revisit status" : "Mark this line to revisit"}
                    onClick={() => void chooseComprehension("unsure")}
                    disabled={comprehensionSaving}
                  >
                    <CircleHelp size={16} /> Revisit
                  </button>
                  <span className="visually-hidden" role="status" aria-live="polite">
                    {comprehensionSaving
                      ? "Saving comprehension status"
                      : state.region.comprehensionStatus === "understood"
                        ? "Marked as understood"
                        : state.region.comprehensionStatus === "unsure"
                          ? "Marked to revisit"
                          : "No comprehension status selected"}
                  </span>
                </div>
                {comprehensionError && <p className="comprehension-error" role="alert">{comprehensionError}</p>}
                <span className="support-options-divider" aria-hidden="true" />
                <button type="button" onClick={beginSentenceEditFromOptions} disabled={!sentence.trim() || sentenceEditing || sentenceSaving || vocabularySaving}>
                  <Pencil size={17} />
                  <span>Correct text</span>
                </button>
                <button type="button" onClick={() => void copySentence()} disabled={!sentence.trim()}>
                  {sentenceCopied ? <Check size={17} /> : <Copy size={17} />}
                  <span>{sentenceCopied ? "Copied line" : "Copy line"}</span>
                </button>
                <button type="button" onClick={() => onRedoRegion(state.region)} disabled={selectionBusy || sentenceSaving || vocabularySaving}>
                  <RotateCcw size={17} />
                  <span>Redraw text area</span>
                </button>
                <button
                  className={`is-danger ${removeArmed ? "is-armed" : ""}`}
                  type="button"
                  onClick={() => void removeCurrentSelection()}
                  disabled={selectionBusy || sentenceSaving || vocabularySaving}
                >
                  {selectionBusy ? <LoaderCircle className="spin" size={17} /> : removeArmed ? <Check size={17} /> : <Trash2 size={17} />}
                  <span>{selectionBusy ? "Removing…" : removeArmed ? "Remove now" : "Remove from page"}</span>
                </button>
              </div>
            </details>
          )}
        </div>
      </header>

      {state.status === "scanning" && (
        <div className="support-working" role="status" aria-live="polite">
          <div className="ocr-scan-visual" aria-hidden="true">
            <ScanText size={30} />
            <span />
          </div>
          <div>
            <strong>{state.warming ? "Preparing text recognition" : "Reading selection"}</strong>
            <p>{state.warming ? "Starting the local OCR model. This only happens after the service restarts." : "Finding the text and preparing dictionary lookups…"}</p>
            <div className="ocr-scan-progress" aria-hidden="true"><span /></div>
          </div>
        </div>
      )}
      {state.status === "error" && (
        <div className="support-error"><ScanText size={24} /><p>{state.message}</p><span>Close this panel and select the speech bubble again.</span></div>
      )}
      {state.status === "done" && (
        <div className="support-result">
          {selectionError && <p className="selection-action-error">{selectionError}</p>}
          {kanjiLiteral && (
            <KanjiDetailPopover
              literal={kanjiLiteral}
              detail={kanjiDetail}
              loading={kanjiLoading}
              error={kanjiError}
              onClose={() => {
                ++kanjiRequest.current;
                setKanjiLiteral(null);
                setKanjiLoading(false);
              }}
            />
          )}
          {sentenceEditing ? (
            <form className="sentence-editor" onSubmit={(event) => {
              event.preventDefault();
              void saveSentenceCorrection();
            }}>
              <label htmlFor={`sentence-correction-${state.region.id}`}>Correct recognized text</label>
              <textarea
                id={`sentence-correction-${state.region.id}`}
                lang="ja"
                value={sentenceDraft}
                onChange={(event) => setSentenceDraft(event.target.value)}
                maxLength={500}
                rows={3}
                autoFocus
                disabled={sentenceSaving}
              />
              <div className="sentence-editor-footer">
                <small>This refreshes words and clears the old translation and explanation.</small>
                <div>
                  <button type="button" onClick={cancelSentenceEdit} disabled={sentenceSaving}>Cancel</button>
                  <button className="is-primary" type="submit" disabled={sentenceSaving || !sentenceDraft.trim() || sentenceDraft.trim() === sentence.trim()}>
                    {sentenceSaving ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
                    {sentenceSaving ? "Saving…" : "Save correction"}
                  </button>
                </div>
              </div>
              {sentenceEditError && <p className="sentence-edit-error">{sentenceEditError}</p>}
            </form>
          ) : (
            <div className="recognized-sentence-row">
              <div className="recognized-sentence" lang="ja">
                {displaySentence ? (
                  <span className={`recognized-dialogue-lines ${detailRegions.length > 1 ? "has-grouped-regions" : ""}`.trim()}>
                    {dialogueLines.map((line) => {
                      const owner = detailSegments.find((segment) => line.start < segment.end && line.end > segment.start)?.region;
                      const active = owner?.id === state.region.id;
                      return (
                        <button
                          className={`recognized-dialogue-line sentence-edit-trigger ${active ? "is-active" : ""}`}
                          type="button"
                          key={`${line.start}-${line.end}`}
                          onClick={() => {
                            if (!owner) return;
                            if (active) beginSentenceEdit();
                            else onActivateRegion(owner);
                          }}
                          aria-pressed={active}
                          title={active ? "Correct this recognized line" : "Select this line"}
                        >
                          {renderSentenceTokens(displaySentence, sentenceTokenSpans, activeTokenSourceIndexes, line)}
                        </button>
                      );
                    })}
                  </span>
                ) : "No text was recognized"}
              </div>
            </div>
          )}
          {copyError && !sentenceEditing && <p className="sentence-copy-error">{copyError}</p>}

          <div className="support-workspace">
            <div className="support-view-tabs" ref={supportTabsRef} role="tablist" aria-label="Reading help">
              {supportViews.map((view) => (
                <button
                  id={`support-tab-${view.id}`}
                  role="tab"
                  type="button"
                  key={view.id}
                  className={`${supportView === view.id ? "selected" : ""} ${view.id === "notes" && noteDirty ? "has-unsaved" : ""}`.trim()}
                  aria-label={view.id === "notes" && noteDirty ? "Notes, unsaved changes" : view.label}
                  aria-selected={supportView === view.id}
                  aria-controls={`support-panel-${view.id}`}
                  tabIndex={supportView === view.id ? 0 : -1}
                  onClick={() => chooseSupportView(view.id)}
                  onKeyDown={(event) => navigateSupportViews(event, view.id)}
                >
                  <span className="support-view-tab-surface" aria-hidden="true" />
                  <span className="support-view-tab-label">{view.label}</span>
                </button>
              ))}
            </div>

            <div className="support-tab-panel" id="support-panel-words" role="tabpanel" aria-labelledby="support-tab-words" hidden={supportView !== "words"}>
          <section className="word-support" aria-labelledby="word-support-heading">
            <div className="support-flow-heading">
              <span id="word-support-heading">Words</span>
              <small>Tap one to look it up</small>
            </div>
            {tokenItems.length > 0 && (
              <div className={`token-picker ${tokenLineGroups.length > 1 ? "has-dialogue-lines" : ""}`.trim()}>
                <div className="token-line-groups">
                  {tokenLineGroups.map((line, lineIndex) => (
                    <div className="token-line-group" key={`${line.start}-${line.end}`}>
                      <div className="token-row" aria-label={`Recognized words for dialogue line ${lineIndex + 1}: ${line.text}`}>
                        {line.tokenIndexes.map(renderTokenButton)}
                      </div>
                    </div>
                  ))}
                </div>
                {primaryPhraseSuggestion ? (
                  <p className="phrase-hint">
                    <span aria-hidden="true">+</span>
                    Tap <strong lang="ja">{primaryPhraseSuggestion.addedSurface}</strong> to look up <strong lang="ja">{primaryPhraseSuggestion.expression}</strong> together.
                  </p>
                ) : activeTokenItems.length > 1 ? (
                  <p className="phrase-hint is-active">
                    Looking up <strong lang="ja">{activeExpression}</strong> as one phrase. Tap a selected word to shorten or restart it.
                  </p>
                ) : !activeTokenRange ? (
                  compoundSpans.length > 0
                    ? <p className="compound-hint"><span aria-hidden="true" /> Dictionary expressions are grouped into one tap. Hold a kanji for details.</p>
                    : <p>Hold a kanji for details.</p>
                ) : (
                  <p>Words can work alone or together. Try tapping a neighbouring word.</p>
                )}
              </div>
            )}

            {activeExpression && <div className="dictionary-result" aria-live="polite">
              {dictionaryLoading ? (
                <div className="dictionary-loading"><LoaderCircle className="spin" size={18} /> Looking up {activeExpression}</div>
              ) : primaryEntry ? (
                <>
                <div className="dictionary-heading">
                  <div lang="ja">
                    <strong>{activeExpression}</strong>
                    {dictionaryMeta && <span>{dictionaryMeta}</span>}
                    {primaryEntry.source === "personal" && (
                      <span className="personal-dictionary-label" lang="en">
                        Your dictionary · {primaryEntry.personalScope === "series" ? "this series" : "everywhere"}
                      </span>
                    )}
                  </div>
                  <div className="dictionary-heading-actions">
                    <JapaneseSpeechButton
                      text={activeExpression ?? ""}
                      reading={primaryEntry.lookupRelation === "potential" ? undefined : primaryEntry.readings[0] ? toHiragana(primaryEntry.readings[0]) : activeReading}
                      mode="word"
                      label={`Hear ${activeExpression ?? "word"} in Japanese`}
                    />
                    <button className={`save-word-button ${saved ? "is-saved" : ""}`} type="button" onClick={() => void saveWord(primaryDefinition)} disabled={saved || vocabularySaving}>
                      {vocabularySaving ? <LoaderCircle className="spin" size={18} /> : saved ? <Check size={18} /> : <BookmarkPlus size={18} />}
                      {vocabularySaving ? "Saving…" : saved ? "Saved" : "Save word"}
                    </button>
                  </div>
                </div>
                <div className="dictionary-senses">
                  {primaryEntry.senses.slice(0, 4).map((sense, index) => (
                    <section key={`${sense.definitions.join("|")}-${index}`}>
                      <div className="dictionary-labels">
                        {index === 0 && primaryEntry.common && <span className="is-common">Common</span>}
                        {index === 0 && primaryEntry.verbForms && primaryEntry.verbForms.length > 0 && (
                          <VerbFormBadges forms={primaryEntry.verbForms} idPrefix={state.region.id} />
                        )}
                        {sense.labels.map((label) => <span className={label === "Archaic" || label === "Obsolete" ? "is-archaic" : ""} key={label}>{label}</span>)}
                      </div>
                      <ol start={index + 1}>
                        <li>{sense.definitions.join("; ")}</li>
                      </ol>
                    </section>
                  ))}
                </div>
                {primaryEntry.source === "personal" && (
                  personalEditing ? (
                    <form className="personal-dictionary-editor" onSubmit={(event) => {
                      event.preventDefault();
                      void savePersonalEdit();
                    }}>
                      <label htmlFor={`personal-meaning-${primaryEntry.id}`}>Your meaning</label>
                      <input
                        id={`personal-meaning-${primaryEntry.id}`}
                        type="text"
                        value={personalDefinitionDraft}
                        onChange={(event) => setPersonalDefinitionDraft(event.target.value)}
                        maxLength={1000}
                        autoComplete="off"
                        autoFocus
                        disabled={vocabularySaving}
                      />
                      <div>
                        <button type="button" onClick={() => {
                          setPersonalEditing(false);
                          setPersonalEditArmed(false);
                          setVocabularyError(null);
                        }} disabled={vocabularySaving}>Cancel</button>
                        <button className="is-danger" type="button" onClick={() => void removePersonalMeaning()} disabled={vocabularySaving}>
                          {personalEditArmed ? "Remove meaning?" : "Remove"}
                        </button>
                        <button className="is-primary" type="submit" disabled={vocabularySaving || !personalDefinitionDraft.trim()}>
                          {vocabularySaving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />}
                          Save
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button className="personal-dictionary-edit-button" type="button" onClick={() => {
                      setPersonalDefinitionDraft(primaryEntry.definitions[0] ?? "");
                      setPersonalEditing(true);
                      setVocabularyError(null);
                    }}>
                      <Pencil size={14} /> Edit your meaning
                    </button>
                  )
                )}
                {primaryEntry.source === "jmdict" && (
                  personalAdding ? (
                    <form className="personal-dictionary-editor personal-dictionary-add-form" onSubmit={(event) => {
                      event.preventDefault();
                      if (customDefinition.trim()) void saveWord(customDefinition, true);
                    }}>
                      <label htmlFor={`personal-meaning-new-${primaryEntry.id}`}>Your meaning</label>
                      <input
                        id={`personal-meaning-new-${primaryEntry.id}`}
                        type="text"
                        value={customDefinition}
                        onChange={(event) => setCustomDefinition(event.target.value)}
                        placeholder="A story-specific meaning"
                        maxLength={1000}
                        autoComplete="off"
                        autoFocus
                        disabled={vocabularySaving}
                      />
                      <fieldset className="custom-definition-scope">
                        <legend>Use this meaning</legend>
                        <div>
                          <button type="button" className={customScope === "series" ? "selected" : ""} aria-pressed={customScope === "series"} onClick={() => setCustomScope("series")}>This manga</button>
                          <button type="button" className={customScope === "global" ? "selected" : ""} aria-pressed={customScope === "global"} onClick={() => setCustomScope("global")}>Everywhere</button>
                        </div>
                      </fieldset>
                      <div>
                        <button type="button" onClick={() => {
                          setPersonalAdding(false);
                          setCustomDefinition("");
                          setVocabularyError(null);
                        }} disabled={vocabularySaving}>Cancel</button>
                        <button className="is-primary" type="submit" disabled={vocabularySaving || !customDefinition.trim()}>
                          {vocabularySaving ? <LoaderCircle className="spin" size={15} /> : <Check size={15} />}
                          Add meaning
                        </button>
                      </div>
                    </form>
                  ) : (
                    <button className="personal-dictionary-edit-button" type="button" onClick={() => {
                      setPersonalAdding(true);
                      setVocabularyError(null);
                    }}>
                      <Pencil size={14} /> Add your meaning
                    </button>
                  )
                )}
                {vocabularyError && <p className="vocabulary-save-error">{vocabularyError}</p>}
                </>
              ) : dictionaryMissing && activeExpression ? (
                <div className="custom-definition">
                <div>
                  <strong lang="ja">{activeExpression}</strong>
                  {activeReading && <span lang="ja">{activeReading}</span>}
                </div>
                <p>No dictionary meaning found. Add your own for a name, place, or story-specific term.</p>
                <form onSubmit={(event) => {
                  event.preventDefault();
                  if (customDefinition.trim()) void saveWord(customDefinition, true);
                }}>
                  <label htmlFor="custom-definition-input">Your meaning</label>
                  <div className="custom-definition-entry">
                    <input
                      id="custom-definition-input"
                      type="text"
                      value={customDefinition}
                      onChange={(event) => setCustomDefinition(event.target.value)}
                      placeholder="e.g. Frieren"
                      maxLength={1000}
                      autoComplete="off"
                    />
                    <button className="save-word-button" type="submit" disabled={!customDefinition.trim() || vocabularySaving}>
                      {vocabularySaving ? <LoaderCircle className="spin" size={18} /> : <BookmarkPlus size={18} />}
                      {vocabularySaving ? "Adding…" : "Add meaning"}
                    </button>
                  </div>
                  <fieldset className="custom-definition-scope">
                    <legend>Use this meaning</legend>
                    <div>
                      <button type="button" className={customScope === "series" ? "selected" : ""} aria-pressed={customScope === "series"} onClick={() => setCustomScope("series")}>This manga</button>
                      <button type="button" className={customScope === "global" ? "selected" : ""} aria-pressed={customScope === "global"} onClick={() => setCustomScope("global")}>Everywhere</button>
                    </div>
                  </fieldset>
                </form>
                {vocabularyError && <p className="vocabulary-save-error">{vocabularyError}</p>}
                </div>
              ) : (
                <p className="dictionary-message">{dictionaryMessage ?? "Looking up this word…"}</p>
              )}
            </div>}
          </section>
          </div>

            <div className="support-tab-panel" id="support-panel-meaning" role="tabpanel" aria-labelledby="support-tab-meaning" hidden={supportView !== "meaning"}>
          <section className="line-meaning" aria-labelledby="line-meaning-heading" aria-live="polite">
            <div className="support-section-heading">
              <div>
                <span id="line-meaning-heading">Meaning</span>
                <small>{translation ? "Codex translation" : "Curated English support"}</small>
              </div>
            </div>
            {translationLoading && <p className="translation-status">Opening the saved translation…</p>}
            {!translationLoading && translation && (
              <article className="translation-copy">
                <strong lang="en">{translation.translation}</strong>
                {translation.notes && <p>{translation.notes}</p>}
                <span className="translation-model-badge is-static">Curated by Codex</span>
              </article>
            )}
            {!translationLoading && !translation && !translationError && <p className="translation-status">This line has not been curated yet.</p>}
            {!translationLoading && translationError && <p className="translation-error">{translationError}</p>}
          </section>

          <section className="grammar-explanation" aria-labelledby="grammar-explanation-heading" aria-live="polite">
            <div className="support-section-heading">
              <div>
                <span id="grammar-explanation-heading">How the Japanese works</span>
                <small>{explanation ? "Codex explanation" : "Curated grammar support"}</small>
              </div>
            </div>
            {explanationLoading && <p className="grammar-explanation-status">Opening the saved explanation…</p>}
            {!explanationLoading && explanation && (
              <div className="grammar-explanation-content">
                <div className="grammar-overview">
                  <p>{explanation.overview}</p>
                </div>
                <ol>
                  {explanation.points.map((point, index) => (
                    <li key={`${point.text}-${index}`}>
                      <div className="grammar-point-heading">
                        <strong lang="ja">{point.text}</strong>
                        <span><span>Pattern</span><b lang="ja">{point.pattern}</b></span>
                      </div>
                      <p className="grammar-point-rule">{point.function}</p>
                      <dl className="grammar-point-details">
                        <div>
                          <dt>Form</dt>
                          <dd className="grammar-formation" lang="ja">{point.formation}</dd>
                        </div>
                        <div>
                          <dt>In this line</dt>
                          <dd>{point.application}</dd>
                        </div>
                      </dl>
                    </li>
                  ))}
                </ol>
                {explanation.nuance && <p className="grammar-nuance"><span>Nuance</span>{explanation.nuance}</p>}
              </div>
            )}
            {!explanationLoading && !explanation && !explanationError && <p className="grammar-explanation-status">This line has not been explained yet.</p>}
            {!explanationLoading && explanationError && <p className="grammar-explanation-error">{explanationError}</p>}
          </section>
          </div>

            <div className="support-tab-panel" id="support-panel-notes" role="tabpanel" aria-labelledby="support-tab-notes" hidden={supportView !== "notes"}>
          <section className="selection-notes" aria-labelledby="selection-notes-heading">
            <div className="support-section-heading">
              <div>
                <span id="selection-notes-heading">Your note</span>
                <small>{noteLoading ? "Loading…" : noteSaving ? "Saving…" : noteDirty ? "Unsaved changes" : hasSavedNote ? "Saved with this selection" : "For this selection"}</small>
              </div>
              <button className="support-section-action" type="button" onClick={() => void saveNote()} disabled={noteLoading || noteSaving || !noteDirty}>
                {noteSaving ? <LoaderCircle className="spin" size={17} /> : !noteDirty && hasSavedNote ? <Check size={17} /> : null}
                {noteSaving ? "Saving" : !noteDirty && hasSavedNote ? "Saved" : "Save note"}
              </button>
            </div>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              placeholder="Add a grammar note, nuance, mnemonic…"
              maxLength={5000}
              disabled={noteLoading}
              aria-label="Notes for this selection"
            />
            {noteError && <p className="selection-note-error">{noteError}</p>}
          </section>
          </div>
          </div>
        </div>
      )}
    </aside>
  );
}

function renderSentenceTokens(
  sentence: string,
  tokenSpans: SentenceTokenSpan[],
  activeSourceIndexes: Set<number>,
  line: DialogueLine = { start: 0, end: sentence.length, text: sentence }
): ReactNode {
  if (activeSourceIndexes.size === 0 || tokenSpans.length === 0) return sentence.slice(line.start, line.end);

  const nodes: ReactNode[] = [];
  let cursor = line.start;

  tokenSpans.filter((span) => span.start < line.end && span.end > line.start).forEach((span) => {
    if (span.start > cursor) nodes.push(sentence.slice(cursor, span.start));
    const tokenText = sentence.slice(span.start, span.end);
    nodes.push(activeSourceIndexes.has(span.sourceIndex)
      ? <mark className="recognized-sentence-token is-active" key={`${span.sourceIndex}-${span.start}`}>{tokenText}</mark>
      : tokenText);
    cursor = span.end;
  });

  if (cursor < line.end) nodes.push(sentence.slice(cursor, line.end));
  return nodes;
}

function isUsefulToken(token: OcrToken): boolean {
  return /[\p{L}\p{N}\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}]/u.test(token.surface);
}

function phraseCandidates(
  tokenItems: Array<{ token: OcrToken; sourceIndex: number }>,
  range: TokenRange | null
): PhraseSuggestion[] {
  if (!range) return [];
  const selectedSurface = tokenItems.slice(range.start, range.end + 1).map(({ token }) => token.surface).join("");
  const candidates: PhraseSuggestion[] = [];
  const left = tokenItems[range.start - 1];
  const first = tokenItems[range.start];
  if (left && first && left.sourceIndex + 1 === first.sourceIndex) {
    candidates.push({
      index: range.start - 1,
      expression: `${left.token.surface}${selectedSurface}`,
      addedSurface: left.token.surface,
      partOfSpeech: [...new Set([...left.token.partOfSpeech, ...tokenItems.slice(range.start, range.end + 1).flatMap(({ token }) => token.partOfSpeech)])]
    });
  }
  const last = tokenItems[range.end];
  const right = tokenItems[range.end + 1];
  if (last && right && last.sourceIndex + 1 === right.sourceIndex) {
    candidates.push({
      index: range.end + 1,
      expression: `${selectedSurface}${right.token.surface}`,
      addedSurface: right.token.surface,
      partOfSpeech: [...new Set([...tokenItems.slice(range.start, range.end + 1).flatMap(({ token }) => token.partOfSpeech), ...right.token.partOfSpeech])]
    });
  }
  return candidates;
}
